import { useCallback } from 'react';
import {
  nextVariableColor,
  generateVariableId,
  VARIABLE_TYPE_ARRAY,
  VARIABLE_TYPE_NUMBER,
  VARIABLE_TYPE_STRING,
  variableNameKey,
  claimDocVariableOntoCampaign,
  docRowScopeChoice,
  presentDocVariable,
  removeDocValueOverlays,
} from '../../../../utils/variableScope';
import { buildVariableOfType } from '../../../../utils/variableTypeChange';

/** Value-cell commits (not rename / type / lock / color). */
const isValueEditPatch = (row, patch) => {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return false;
  const hasValue =
    Object.prototype.hasOwnProperty.call(patch, 'value') ||
    Object.prototype.hasOwnProperty.call(patch, 'values');
  if (!hasValue) return false;
  if (Object.prototype.hasOwnProperty.call(patch, 'name')) return false;
  if (Object.prototype.hasOwnProperty.call(patch, 'type')) {
    const current = row?.type || VARIABLE_TYPE_STRING;
    if (patch.type !== current) return false;
  }
  return true;
};

export default function useVariableMutations({
  hasDocScope,
  inheritedRows,
  docRowsRef,
  campaignRowsRef,
  onDocRef,
  onCampRef,
  missionRowsRef,
  onMissionRef,
  onDocVariablesChange,
  onCampaignVariablesChange,
  newRowIdRef,
  campaign = null,
  allSprints = [],
}) {
  const applyRowPatch = (row, patch) => {
    const merged = { ...row, ...patch };
    if (merged.type === VARIABLE_TYPE_STRING || merged.type === VARIABLE_TYPE_NUMBER) {
      delete merged.values;
    }
    return merged;
  };

  const listChanged = (prev, next) =>
    !prev ||
    prev.length !== next.length ||
    prev.some((row, i) => row !== next[i]);

  const writeRows = (ref, onChange, next, silent) => {
    if (!onChange || !listChanged(ref.current, next)) return;
    ref.current = next;
    onChange(next, { silent });
  };

  const onPatch = useCallback((scope, id, patch, { silent = false } = {}) => {
    // Mission scope keeps the value on this mission. Document scope saves it
    // on the document. Name / type / lock stay on the declaration.
    if (scope === 'doc' && onDocRef.current) {
      const row = docRowsRef.current.find((v) => v.id === id);
      if (row && isValueEditPatch(row, patch)) {
        const choice = docRowScopeChoice(row, missionRowsRef?.current);
        if (choice === 'mission' && onMissionRef?.current && missionRowsRef) {
          const nextMission = claimDocVariableOntoCampaign(
            missionRowsRef.current,
            row,
            patch
          );
          writeRows(missionRowsRef, onMissionRef.current, nextMission, silent);
          if (row.valueScope !== 'mission') {
            const nextDocs = docRowsRef.current.map((v) =>
              v.id === id ? { ...v, valueScope: 'mission' } : v
            );
            writeRows(docRowsRef, onDocRef.current, nextDocs, silent);
          }
          return;
        }
        const nextDocs = docRowsRef.current.map((v) =>
          v.id === id ? { ...applyRowPatch(v, patch), valueScope: 'doc' } : v
        );
        writeRows(docRowsRef, onDocRef.current, nextDocs, silent);
        if (onMissionRef?.current && missionRowsRef) {
          writeRows(
            missionRowsRef,
            onMissionRef.current,
            removeDocValueOverlays(missionRowsRef.current, row),
            silent
          );
        }
        if (onCampRef.current) {
          writeRows(
            campaignRowsRef,
            onCampRef.current,
            removeDocValueOverlays(campaignRowsRef.current, row),
            silent
          );
        }
        return;
      }
    }
    if (scope === 'doc' && onDocRef.current) {
      const next = docRowsRef.current.map((v) => (v.id === id ? applyRowPatch(v, patch) : v));
      docRowsRef.current = next;
      onDocRef.current(next, { silent });
    } else if (scope === 'campaign' && onCampRef.current) {
      const next = campaignRowsRef.current.map((v) =>
        v.id === id ? applyRowPatch(v, patch) : v
      );
      campaignRowsRef.current = next;
      onCampRef.current(next, { silent });
    }
  }, [docRowsRef, campaignRowsRef, missionRowsRef, onDocRef, onCampRef, onMissionRef]);

  const onScopeChange = useCallback((scope, id, newScope) => {
    if (newScope === scope) return;
    const docs = docRowsRef.current || [];
    const camps = campaignRowsRef.current || [];
    const missions = missionRowsRef?.current || [];
    const ctx = {
      campaign: campaign ? { ...campaign, variables: camps } : null,
      allSprints,
      missionVariables: missionRowsRef ? missions : null,
    };
    const shownPatch = (row) => {
      const shown = presentDocVariable(row, ctx);
      const patch = { value: shown?.value ?? '' };
      if (Array.isArray(shown?.values)) {
        patch.values = [...shown.values];
        if (shown.type) patch.type = shown.type;
      }
      return patch;
    };
    const asStoredRow = (row, patch) => {
      const next = { ...row, ...patch };
      delete next.scope;
      delete next.sourceId;
      delete next.sourceName;
      delete next.shadowed;
      delete next.docOverlay;
      return next;
    };

    if ((scope === 'doc' || scope === 'mission') && (newScope === 'doc' || newScope === 'mission')) {
      const variable = docs.find((v) => v.id === id);
      if (!variable || !onDocRef.current) return;
      if (newScope === 'mission') {
        if (!onMissionRef?.current || !missionRowsRef) return;
        const nextDocs = docs.map((v) =>
          v.id === id ? { ...v, valueScope: 'mission' } : v
        );
        const nextMission = claimDocVariableOntoCampaign(
          missions,
          variable,
          shownPatch(variable)
        );
        docRowsRef.current = nextDocs;
        missionRowsRef.current = nextMission;
        onDocRef.current(nextDocs);
        onMissionRef.current(nextMission);
        return;
      }
      const nextDocs = docs.map((v) =>
        v.id === id ? { ...v, ...shownPatch(variable), valueScope: 'doc' } : v
      );
      docRowsRef.current = nextDocs;
      onDocRef.current(nextDocs);
      if (onMissionRef?.current && missionRowsRef) {
        const nextMission = removeDocValueOverlays(missions, variable);
        missionRowsRef.current = nextMission;
        onMissionRef.current(nextMission);
      }
      if (onCampRef.current) {
        const nextCamps = removeDocValueOverlays(camps, variable);
        campaignRowsRef.current = nextCamps;
        onCampRef.current(nextCamps);
      }
      return;
    }

    if ((scope === 'doc' || scope === 'mission') && newScope === 'campaign') {
      const variable = docs.find((v) => v.id === id);
      if (!variable || !onDocRef.current || !onCampRef.current) return;
      const moved = asStoredRow(variable, shownPatch(variable));
      delete moved.valueScope;
      const nextDocs = docs.filter((v) => v.id !== id);
      const nextCamps = [...removeDocValueOverlays(camps, variable), moved];
      docRowsRef.current = nextDocs;
      campaignRowsRef.current = nextCamps;
      onDocRef.current(nextDocs);
      onCampRef.current(nextCamps);
      if (onMissionRef?.current && missionRowsRef) {
        const nextMission = removeDocValueOverlays(missions, variable);
        missionRowsRef.current = nextMission;
        onMissionRef.current(nextMission);
      }
      return;
    }

    if (scope === 'campaign' && (newScope === 'doc' || newScope === 'mission')) {
      const variable = camps.find((v) => v.id === id);
      if (!variable || !onDocRef.current || !onCampRef.current) return;
      if (newScope === 'mission' && (!onMissionRef?.current || !missionRowsRef)) return;
      const nextCamps = camps.filter((v) => v.id !== id);
      const moved = asStoredRow(variable, {});
      delete moved.valueScope;
      if (newScope === 'mission') moved.valueScope = 'mission';
      else moved.valueScope = 'doc';
      const nextDocs = [...docs, moved];
      campaignRowsRef.current = nextCamps;
      docRowsRef.current = nextDocs;
      onCampRef.current(nextCamps);
      onDocRef.current(nextDocs);
      if (newScope === 'mission') {
        const nextMission = claimDocVariableOntoCampaign(missions, moved, {
          value: moved.value ?? '',
          ...(Array.isArray(moved.values)
            ? { values: [...moved.values], type: moved.type }
            : {}),
        });
        missionRowsRef.current = nextMission;
        onMissionRef.current(nextMission);
      }
    }
  }, [
    docRowsRef,
    campaignRowsRef,
    missionRowsRef,
    onDocRef,
    onCampRef,
    onMissionRef,
    campaign,
    allSprints,
  ]);

  const onDelete = useCallback((scope, id) => {
    if (scope === 'doc' && onDocRef.current) {
      const next = docRowsRef.current.filter((v) => v.id !== id);
      docRowsRef.current = next;
      onDocRef.current(next);
    } else if (scope === 'campaign' && onCampRef.current) {
      const next = campaignRowsRef.current.filter((v) => v.id !== id);
      campaignRowsRef.current = next;
      onCampRef.current(next);
    }
  }, [docRowsRef, campaignRowsRef, onDocRef, onCampRef]);

  const onToggleLock = useCallback((scope, id) => {
    if (scope === 'doc' && onDocRef.current) {
      const next = docRowsRef.current.map((v) =>
        v.id === id ? { ...v, locked: !v.locked } : v
      );
      docRowsRef.current = next;
      onDocRef.current(next);
    } else if (scope === 'campaign' && onCampRef.current) {
      const next = campaignRowsRef.current.map((v) =>
        v.id === id ? { ...v, locked: !v.locked } : v
      );
      campaignRowsRef.current = next;
      onCampRef.current(next);
    }
  }, [docRowsRef, campaignRowsRef, onDocRef, onCampRef]);

  const handleAdd = (type = 'string') => {
    const id = generateVariableId();
    newRowIdRef.current = id;
    const docs = docRowsRef.current;
    const camps = campaignRowsRef.current;
    const base = {
      id,
      name: '',
      description: '',
      color: nextVariableColor([...docs, ...camps, ...inheritedRows]),
      locked: false,
    };
    const next =
      type === VARIABLE_TYPE_ARRAY
        ? { ...base, type: VARIABLE_TYPE_ARRAY, values: [''], value: '' }
        : type === VARIABLE_TYPE_NUMBER
          ? { ...base, type: VARIABLE_TYPE_NUMBER, value: '' }
          : { ...base, value: '' };

    if (hasDocScope) {
      const list = [...docs, next];
      docRowsRef.current = list;
      onDocVariablesChange(list);
    } else if (onCampaignVariablesChange) {
      const list = [...camps, next];
      campaignRowsRef.current = list;
      onCampaignVariablesChange(list);
    }
  };

  const handleCreateVariable = useCallback(
    (name, type) => {
      const trimmed = String(name || '').trim().replace(/\s+/g, '_');
      if (!trimmed) return;
      const docs = docRowsRef.current;
      const camps = campaignRowsRef.current;
      const exists = [...docs, ...camps, ...inheritedRows].some(
        (v) => variableNameKey(v.name) === variableNameKey(trimmed)
      );
      if (exists) return;
      const next = buildVariableOfType(
        {
          id: generateVariableId(),
          name: trimmed,
          description: '',
          color: nextVariableColor([...docs, ...camps, ...inheritedRows]),
          locked: false,
        },
        type
      );
      if (hasDocScope && onDocRef.current) {
        const list = [...docs, next];
        docRowsRef.current = list;
        onDocRef.current(list);
      } else if (onCampRef.current) {
        const list = [...camps, next];
        campaignRowsRef.current = list;
        onCampRef.current(list);
      }
    },
    [hasDocScope, inheritedRows, docRowsRef, campaignRowsRef, onDocRef, onCampRef]
  );

  return {
    onPatch,
    onScopeChange,
    onDelete,
    onToggleLock,
    handleAdd,
    handleCreateVariable,
  };
}
